import { Toast } from '@easyvaas/js-components';
import classNames from 'classnames';
import { reduce } from 'ramda';
import React, { useEffect, useMemo } from 'react';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import SmallText from 'src/components/SmallText';
import { useCheckMaterialEnough } from '../../hooks/useCheckMaterialEnough';
import { useClosePopup } from '../../hooks/useClosePopup';
import { useOpenPopup } from '../../hooks/useOpenPopup';
import { evolveImage, imagesV3Data } from '../../services';
import { imageItemSelector, pieceItemSelector } from '../../store/selector';
import {
  evolutionAdditionsState,
  evolutionProbState,
  imagePopupState,
  imagesDataState,
  resultState
} from '../../store/state';
import ImageOperationPopup from '../ImageOpreationPopup';
import AdditionItem from './AdditionItem';

import styles from './index.module.scss';

const NormalEvoFooter = React.memo(() => {
  const prob = useRecoilValue(evolutionProbState);
  return (
    <div className={styles.footer}>
      <div className={styles.subtitle}>添加女神祝福 增加成功率</div>
      <div className={styles.additions}>
        <AdditionItem type={1} index={0} />
        <AdditionItem type={1} index={1} />
        <AdditionItem type={1} index={2} />
        <AdditionItem type={1} index={3} />
        <AdditionItem type={1} index={4} />
      </div>
      <div className={styles.prob}>
        当前成功率
        <span className={styles.value}>{prob.base + prob.extra}%</span>
        <span className={styles.plus}>
          <span>
            {prob.base}%+{prob.extra}%
          </span>
        </span>
      </div>
    </div>
  );
});

const AdvancedEvoFooter = React.memo(() => {
  const prob = useRecoilValue(evolutionProbState);
  return (
    <div className={styles.footer}>
      <div className={styles.prob}>
        当前成功率<span className={styles.value}>{prob.base}%</span>
      </div>
    </div>
  );
});

const renderFooter = (type: 1 | 2, piece?: ImageV3API.Material) => {
  if (type === 1) {
    return (
      <p className={styles.description}>
        1.每格祝福可使用2000金币或5碎片，增加5%成功率
        <br />
        2.如合成成功{piece?.title}全部消耗，低级座驾不会消失
        <br />
        3.如合成失败，无祝福情况下{piece?.title}
        全部消耗，每添加一格祝福可减少20%
        {piece?.title}消耗，5格祝福放满升级失败不会消耗{piece?.title}。
        <br />
        4.基础合成成功率是50%，每次合成失败增加5%基础合成成功率，合成成功率最高为100%。
      </p>
    );
  }
  return (
    <p className={styles.description}>
      1.高级进阶只能使用进阶卡
      <br />
      2.进阶卡获取途径：参加平台活动奖励发放，帮主播完成心愿最多的用户和主播概率掉落，开通守护和进阶守护概率掉落，打赏贵族礼物概率掉落
      <br />
      3.进阶卡分类：黄色进阶卡：20%进阶成功，绿色进阶卡：40%进阶成功，蓝色进阶卡：60%进阶成功，紫色进阶卡：80%进阶成功，金色进阶卡：100%进阶成功
    </p>
  );
};

const EvolutionPopup = () => {
  const { visible, target } = useRecoilValue(imagePopupState('evolution'));
  const setProb = useSetRecoilState(evolutionProbState);
  const image = useRecoilValue(imageItemSelector(target));
  const open = useOpenPopup();
  const close = useClosePopup();
  const piece = useRecoilValue(pieceItemSelector(image?.piecesType || 0));
  const checkMaterial = useCheckMaterialEnough();
  const onClick = useRecoilCallback(({ snapshot, set }) => async () => {
    try {
      if (!image || !image.advanceInfo) return;
      const additions = snapshot.getLoadable(evolutionAdditionsState);
      const data = snapshot.getLoadable(imagesDataState);
      if (data.state !== 'hasValue' || additions.state !== 'hasValue') return;
      const genParams = (params: ImageV3API.EvolutionParams) =>
        reduce(
          (acc, elm) => {
            const material = data.contents.entities.materials[elm];
            if (!material) return acc;
            if (material.specialExtend?.piecesType === 0) {
              return { ...acc, blessingNumber: (acc.blessingNumber || 0) + 5 };
            } else {
              return { ...acc, cost: (acc.cost += 2000) };
            }
          },
          params,
          additions.contents
        );
      let params: ImageV3API.EvolutionParams = {
        imgId: image.id,
        cost: 0,
        number: 0
      };
      if (image.level < 3) {
        //普通进阶
        if (!piece || piece.specialExtend?.piecesType === undefined) return;
        if (!checkMaterial(piece, image.advanceInfo.advanceNumber)) return;
        params.number = image.advanceInfo.advanceNumber || 0; //设置初始碎片数量
        params = genParams(params); //获取总的金币和碎片
      } else {
        //高级进阶
        if (additions.contents.length === 0) {
          Toast('请选择进阶卡');
          return;
        }
        params.toolId = additions.contents[0];
      }
      Toast.loading('进阶中...');
      const res = await evolveImage(params);
      await imagesV3Data.refresh({});
      close('evolution');
      set(resultState, res);
      open('result', image.id, 1);
      Toast.hide();
    } catch ({ code, message }) {
      Toast(message);
    }
  });
  const addition = useMemo(() => {
    if (!image) return null;
    if (image.level < 3) {
      if (!piece) return null;
      return (
        <>
          <div
            className={styles.piece}
            style={{ backgroundImage: `url(${piece.logo})` }}
          >
            <div className={styles.count}>
              <span>{image.advanceInfo?.advanceNumber}</span>
            </div>
          </div>
          <SmallText fontSize={20} className={styles.name} inline>
            {piece.title}
          </SmallText>
        </>
      );
    } else {
      return <AdditionItem type={2} />;
    }
  }, [image, piece]);
  useEffect(() => {
    if (!visible || !image || !image.advanceInfo) return;
    if (image.level < 3) {
      setProb({
        base: 50 + image.advanceInfo.failedTimes * 10,
        extra: 0
      });
    } else {
      setProb({ base: 0, extra: 0 });
    }
  }, [image, setProb, visible]);

  if (!image || !image.advanceInfo) return null;

  return (
    <ImageOperationPopup
      type="evolution"
      title={`${image.level === 3 ? '高级' : ''}座驾进阶`}
      position="bottom"
      className={classNames(
        styles.container,
        styles[`type${image.level === 3 ? 2 : 1}`]
      )}
      confirmText="进阶"
      onClick={onClick}
      renderFooter={() => renderFooter(image.level === 3 ? 2 : 1, piece)}
      popupClassName={styles.popup}
      id="evolution-content"
    >
      <img src={image.url} alt="" className={styles.item} />
      <div className={styles.addition}>{addition}</div>
      <img src={image.advanceInfo.url} alt="" className={styles.item} />
      {image.level === 3 ? <AdvancedEvoFooter /> : <NormalEvoFooter />}
    </ImageOperationPopup>
  );
};

export default React.memo(EvolutionPopup);
